/**
 * Aktivitaeten (Fachmodul "crm").
 *
 * Die vollstaendige Kontakthistorie ueber alle Partner, filterbar nach
 * Art und Status. Die Historie eines einzelnen Kunden steht zusaetzlich
 * in dessen Akte.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  CRM_ACTIVITY_KIND_LABELS,
  CRM_ACTIVITY_KINDS,
  formatDate,
  type CrmActivityKind,
} from '@erp/shared';

import { api, withQuery } from '../api/client';
import type { ListResponse } from '../api/types';
import { useAuth } from '../auth';
import {
  ActivityDetailDialog,
  CreateActivityDialog,
  kindBadge,
  type ActivityRow,
} from '../components/CrmActivity';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyState, LoadingBlock, TextInput } from '../components/ui';

export function CrmActivitiesPage() {
  const { can } = useAuth();

  const [kindFilter, setKindFilter] = useState<CrmActivityKind | ''>('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [openRow, setOpenRow] = useState<ActivityRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-activities', 'history', kindFilter, search],
    queryFn: () =>
      api.get<ListResponse<ActivityRow>>(
        withQuery('/api/crm/activities', { kind: kindFilter, q: search, limit: 200 }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Aktivitäten"
        actions={
          can('crm:write') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Aktivität erfassen
            </button>
          ) : undefined
        }
      />

      <div className="row" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn btn-sm${kindFilter === '' ? ' btn-primary' : ''}`}
          onClick={() => setKindFilter('')}
        >
          Alle
        </button>
        {CRM_ACTIVITY_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`btn btn-sm${kindFilter === kind ? ' btn-primary' : ''}`}
            onClick={() => setKindFilter(kind)}
          >
            {CRM_ACTIVITY_KIND_LABELS[kind]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <TextInput value={search} onChange={setSearch} placeholder="Betreff, Partner, Notizen …" />
      </div>

      <div className="card">
        {isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Keine Aktivitäten gefunden."
            hint={'Neue Einträge entstehen über „Aktivität erfassen" oder aus der Kundenakte.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Datum</th>
                  <th className="shrink">Art</th>
                  <th>Partner</th>
                  <th>Betreff</th>
                  <th>Zuständig</th>
                  <th className="shrink">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} onClick={() => setOpenRow(row)} style={{ cursor: 'pointer' }}>
                    <td>{formatDate(row.activityDate)}</td>
                    <td>{kindBadge(row.kind)}</td>
                    <td>
                      <Link
                        to={`${row.partnerIsCustomer ? '/kunden' : '/lieferanten'}/${row.partnerId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.partnerName}
                      </Link>
                    </td>
                    <td
                      style={{
                        maxWidth: 340,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.subject}
                    </td>
                    <td>{row.assignedToName ?? <span className="faint">—</span>}</td>
                    <td>
                      {row.status === 'open' ? (
                        <Badge kind="warning">Offen</Badge>
                      ) : (
                        <Badge kind="success">Erledigt</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? <CreateActivityDialog onClose={() => setCreating(false)} /> : null}
      {openRow ? <ActivityDetailDialog activity={openRow} onClose={() => setOpenRow(null)} /> : null}
    </>
  );
}
